import { Injectable, Directive } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  Validator
} from '@angular/forms';

interface TypedControlValueAccessor<T> extends ControlValueAccessor {
  writeValue(obj: T | null): void;
  registerOnChange(fn: (_: T | null) => void): void;
  registerOnTouched(fn: () => void): void;
}

export interface TypedValueControl<T> extends AbstractControl {
  value: Readonly<T>;
  patchValue(value: T | null, options?: object | undefined): void;
}

// export interface SetValueOptions {
//   onlySelf?: boolean;
//   emitEvent?: boolean;
//   emitModelToViewChange?: boolean;
//   emitViewToModelChange?: boolean;
// }

//it makes no sense that this decorator is required, but it is
@Directive()
@Injectable()
export abstract class FormChildBase<TModelValues, TFormGroupValues>
  implements TypedControlValueAccessor<TModelValues>, Validator
{
  readonly form: FormGroup;
  private model: TModelValues | null;
  private currentInstanceFormControl: AbstractControl | null = null;
  private _isFormDisabledByParent = false;
  //**HACK: THIS FUNCTION IS DESIGNED TO HELP WORK AROUND AN APPARENT TIMING ISSUE WITH PARENT FORM VALIDATION STATE WHEN CONTROLS ARE ADDED DYNAMICALLY (I.E. FORMARRAYS)*/
  protected forceValidationUpdateOnAncestorForms() {
    setTimeout(() => {
      if (this.currentInstanceFormControl) {
        this.currentInstanceFormControl.updateValueAndValidity();
      }
    }, 0);
  }
  constructor(fb: FormBuilder) {
    this.form = this.generateEmptyForm(fb);
    this.form.valueChanges.subscribe(() => {
      this.emitChangeToParent();
    });
  }

  private generateModelFromForm(): TModelValues {
    let modelToReturn = this.generateCopyOfModel(this.model);
    modelToReturn = this.updateModelCopyUsingFormValues(modelToReturn, this.form.value);
    return modelToReturn;
  }

  protected abstract generateFormGroupValuesFromModel(model: TModelValues | null): TFormGroupValues;

  protected abstract generateCopyOfModel(model: TModelValues | null): TModelValues;

  protected abstract updateModelCopyUsingFormValues(
    modelCopy: TModelValues,
    formValues: TFormGroupValues
  ): TModelValues;

  protected abstract generateEmptyForm(fb: FormBuilder): FormGroup;

  protected emitChangeToParent() {
    const updatedModel = this.generateModelFromForm();
    this.changeEmitter(updatedModel);
  }

  private changeEmitter = (_: TModelValues | null) => {};
  private touchEmitter = () => {};

  validate(c: AbstractControl): {
    [key: string]: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  } | null {
    //hack.
    //fixes timing issue where a child of this form attaches itself along with its validation rules
    //but the current instance control/component has already had "validate" called for this change detection cycle
    if (!this.currentInstanceFormControl) {
      this.currentInstanceFormControl = c;
    }
    return this.form.valid ? null : { invalid: true };
  }
  writeValue(obj: TModelValues | null): void {
    this.model = obj;
    this.applyFormGroupValuesToForm(this.form, this.generateFormGroupValuesFromModel(this.model));
  }

  protected applyFormGroupValuesToForm(form: FormGroup, formValues: TFormGroupValues): void {
    form.patchValue(formValues as Object);
  }

  registerOnChange(fn: (_: TModelValues | null) => void): void {
    this.changeEmitter = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.touchEmitter = fn;
  }

  protected emitBlur() {
    this.touchEmitter();
  }

  protected get isFormDisabledByParent() {
    return this._isFormDisabledByParent;
  }
  setDisabledState(isDisabled: boolean) {
    this._isFormDisabledByParent = isDisabled;
    isDisabled ? this.form.disable() : this.form.enable();
  }
  //I don't think this is used any more, but is intended to allow a parent
  //to set values with options that get propagated down to the child
  //this information is not available to control value accessors
  //due to limitations in the angular reactive form library
  //https://github.com/angular/angular/issues/20230

  // setValue(model: TModelValues, options: SetValueOptions = {}): void {
  //   const formGroupValues = this.generateFormGroupValuesFromModel(model);
  //   this.form.setValue(formGroupValues, options);
  // }
}
